import * as z from "zod";

import { InvitationStatus, MemberRole } from "@workspace/auth";
import { offsetPaginationSchema } from "@workspace/shared/schema";

import {
  rangeQuerySchema,
  multiValueQuerySchema,
  sortQuerySchema,
} from "./shared";

export const getMembersInputSchema = offsetPaginationSchema.extend({
  sort: sortQuerySchema.optional(),
  q: z.string().optional(),
  role: multiValueQuerySchema(z.enum(MemberRole)).optional(),
  createdAt: rangeQuerySchema.optional(),
});

export type GetMembersInput = z.infer<typeof getMembersInputSchema>;

export const getMembersResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      organizationId: z.string(),
      role: z.enum(MemberRole),
      createdAt: z.coerce.date(),
      userId: z.string(),
      user: z.object({
        id: z.string(),
        name: z.string(),
        email: z.string(),
        image: z
          .string()
          .nullish()
          .transform((val) => (val === null ? undefined : val)),
      }),
    }),
  ),
  total: z.number(),
});

export type GetMembersResponse = z.infer<typeof getMembersResponseSchema>;

export const getInvitationsInputSchema = offsetPaginationSchema.extend({
  sort: sortQuerySchema.optional(),
  email: z.string().optional(),
  role: multiValueQuerySchema(z.enum(MemberRole)).optional(),
  status: multiValueQuerySchema(z.enum(InvitationStatus)).optional(),
  expiresAt: rangeQuerySchema.optional(),
});

export type GetInvitationsInput = z.infer<typeof getInvitationsInputSchema>;

export const getInvitationsResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      organizationId: z.string(),
      email: z.string(),
      role: z.enum(MemberRole),
      expiresAt: z.coerce.date(),
      createdAt: z.coerce.date(),
      inviterId: z.string(),
      status: z.enum(InvitationStatus),
    }),
  ),
  total: z.number(),
});

export type GetInvitationsResponse = z.infer<
  typeof getInvitationsResponseSchema
>;

export const updateGoogleFormResponseSourceInputSchema = z.object({
  url: z
    .string()
    .trim()
    .max(2048)
    .refine(
      (value) => value === "" || /^https?:\/\//i.test(value),
      "Must be a valid URL",
    ),
});

export type UpdateGoogleFormResponseSourceInput = z.infer<
  typeof updateGoogleFormResponseSourceInputSchema
>;

export const aiWritingLanguageSchema = z.enum(["en-GB", "en-US", "en-AU"]);

const bannedWordsSchema = z
  .array(z.string().trim().min(1).max(200))
  .max(500)
  .superRefine((values, context) => {
    const seen = new Set<string>();
    values.forEach((value, index) => {
      const key = value.toLocaleLowerCase("en-GB");
      if (seen.has(key)) {
        context.addIssue({
          code: "custom",
          path: [index],
          message: "Duplicate banned word or phrase",
        });
      }
      seen.add(key);
    });
  });

export const updateAIWritingPreferencesInputSchema = z.object({
  language: aiWritingLanguageSchema,
  bannedWords: bannedWordsSchema,
});

export const aiWritingPreferencesResponseSchema = z.object({
  language: aiWritingLanguageSchema,
  bannedWords: z.array(z.string()),
  defaultBannedWords: z.array(z.string()),
});

export type UpdateAIWritingPreferencesInput = z.infer<
  typeof updateAIWritingPreferencesInputSchema
>;

export type AIWritingPreferencesResponse = z.infer<
  typeof aiWritingPreferencesResponseSchema
>;

export const getGoogleFormResponseSourceResponseSchema = z.object({
  url: z.string().nullish(),
});

export type GetGoogleFormResponseSourceResponse = z.infer<
  typeof getGoogleFormResponseSourceResponseSchema
>;

export const googleFormResponseEntrySchema = z.object({
  id: z.string(),
  submittedAt: z.string().nullish(),
  vesselName: z.string().nullish(),
  make: z.string().nullish(),
  model: z.string().nullish(),
  year: z.number().int().nullish(),
  loa: z.string().nullish(),
  beam: z.string().nullish(),
  draft: z.string().nullish(),
  hullMaterial: z.string().nullish(),
  fuel: z.string().nullish(),
  engine: z.string().nullish(),
  mouldNumber: z.string().nullish(),
  officialNumber: z.string().nullish(),
  ssrNumber: z.string().nullish(),
  registeredTonnage: z.string().nullish(),
  hin: z.string().nullish(),
  clientName: z.string().nullish(),
  location: z.string().nullish(),
  surveyType: z.string().nullish(),
});

export type GoogleFormResponseEntry = z.infer<
  typeof googleFormResponseEntrySchema
>;

export const listGoogleFormResponsesResponseSchema = z.object({
  configured: z.boolean(),
  entries: z.array(googleFormResponseEntrySchema),
});

export type ListGoogleFormResponsesResponse = z.infer<
  typeof listGoogleFormResponsesResponseSchema
>;
